/**
 * Road surfaces and markings, drawn in the fragment shader.
 *
 * Every ribbon vertex carries its side of the road, the distance along it, its
 * style and its half-width (see `core/build/roads.ts`). From those four
 * numbers the shader draws asphalt with edge lines and dashed lanes on the big
 * roads, paving joints on pedestrian streets, a grass median down a farm
 * track, sleepers and rails on a railway — all in one draw call per tile and
 * with no marking geometry at all. The style ids match `ROAD_STYLE` in
 * `core/sources/shortbread.ts`.
 *
 * Lanes are painted where the traffic drives them: `roadLanes` reads a road's
 * width the way `laneLayout` in `core/traffic/lanes.ts` does. A one-way
 * carriageway, narrower than its class's two-way road, gets its lanes side by
 * side with dashes between them and solid lines along their outer edges. A
 * two-way road gets a centre line, with dashes between the lanes each way.
 *
 * Zebra crossings come from a second attribute, `aZebra`: how far along its
 * road each of up to two crossings nearby is, plus one, or 0 for none (see
 * `core/build/zebras.ts`). A ribbon without it has none.
 */

import { Color, DoubleSide, MeshStandardMaterial, type WebGLProgramParametersWithUniforms } from 'three';
import { GLSL_LAMP_LIGHT, floraNight, lampLight } from './flora.js';
import { terrainWeather } from './terrainMaterial.js';

const GLSL_ROAD = /* glsl */ `
float roadHash(vec2 p) { return fract(sin(dot(p, vec2(12.9898, 78.233))) * 43758.5453); }
float roadNoise(vec2 p) {
  vec2 i = floor(p); vec2 f = fract(p); vec2 w = f * f * (3.0 - 2.0 * f);
  return mix(mix(roadHash(i), roadHash(i + vec2(1.0, 0.0)), w.x),
             mix(roadHash(i + vec2(0.0, 1.0)), roadHash(i + vec2(1.0, 1.0)), w.x), w.y);
}
// A line of the given width centred on 'centre', antialiased by screen footprint.
float roadLine(float x, float centre, float width) {
  float aa = max(fwidth(x), 0.001);
  return 1.0 - smoothstep(width * 0.5 - aa, width * 0.5 + aa, abs(x - centre));
}
// 1 on the dash, 0 in the gap.
float roadDash(float along, float on, float period) {
  float m = mod(along, period);
  float aa = max(fwidth(along), 0.001);
  return smoothstep(0.0, aa, m) * (1.0 - smoothstep(on - aa, on + aa, m));
}
// Whether the fragment is drawn: all of a road, only the rails of a tramway.
bool roadVisible(int style, float x) {
  return style != 12 || abs(abs(x) - 0.72) < 0.065;
}
// How a road of this style and half-width is laid out, as laneLayout has it:
// x = 1 if one-way, y = lanes (each way when two-way), z = lane width.
vec3 roadLanes(int style, float hw) {
  float full = style == 0 ? 7.5 : style == 1 ? 5.5 : style == 2 ? 4.25 : 2.9;
  if (hw < full - 0.05) {
    float n = clamp(floor(hw * 2.0 / 4.5), 1.0, 3.0);
    return vec3(1.0, n, min(3.5, hw * 2.0 / n));
  }
  float n = hw >= 7.0 ? 2.0 : 1.0;
  return vec3(0.0, n, min(3.5, hw / n));
}
// Dashed lines between lanes running the same way: across a one-way
// carriageway, or within each half of a two-way road.
float roadDividers(vec3 lanes, float x, float along, float width, float on, float period) {
  float m = 0.0;
  for (int k = 1; k < 3; k++) {
    if (float(k) >= lanes.y) break;
    float at = lanes.x > 0.5 ? (float(k) - lanes.y * 0.5) * lanes.z : float(k) * lanes.z;
    m = max(m, lanes.x > 0.5 ? roadLine(x, at, width) : roadLine(abs(x), at, width));
  }
  return m * roadDash(along, on, period);
}
// A zebra crossing: white bars half a metre wide, half a metre apart, three
// metres long with the traffic, kerb to kerb, at each crossing's distance
// along the road (stored plus one; 0 is none).
float roadZebra(vec2 crossings, float x, float along, float hw) {
  float m = 0.0;
  for (int i = 0; i < 2; i++) {
    float at = i == 0 ? crossings.x : crossings.y;
    if (at < 0.5) continue;
    m = max(m, roadLine(along, at - 1.0, 3.0) * roadDash(x + 64.25, 0.5, 1.0));
  }
  return m * (1.0 - smoothstep(hw - 0.55, hw - 0.4, abs(x)));
}
vec3 roadColour(int style, float x, float along, float hw, vec2 world, vec2 zebra) {
  float grain = 0.93 + 0.1 * roadNoise(world * 1.7);
  vec3 white = vec3(0.82, 0.83, 0.82);
  float edge = abs(x);
  vec3 c;
  if (style <= 4) {
    // Asphalt, lighter for the smaller classes, with a pale kerb.
    vec3 tone = style == 0 ? vec3(0.088, 0.098, 0.111)
      : style == 1 ? vec3(0.114, 0.125, 0.138)
      : style == 2 ? vec3(0.138, 0.15, 0.162)
      : style == 3 ? vec3(0.178, 0.188, 0.198) : vec3(0.219, 0.227, 0.238);
    c = tone * grain;
    if (style <= 3) c = mix(c, vec3(0.42, 0.42, 0.4), smoothstep(hw - 0.28, hw - 0.18, edge));
    vec3 lanes = roadLanes(style, hw);
    bool oneway = lanes.x > 0.5;
    // The solid line along the outer edge of the outermost lanes.
    float outer = oneway ? min(lanes.y * lanes.z * 0.5 + 0.1, hw - 0.45) : hw - (style == 0 ? 0.6 : 0.5);
    if (style == 0) {
      c = mix(c, white, roadLine(edge, outer, 0.2));
      c = mix(c, white, roadDividers(lanes, x, along, 0.15, 3.0, 12.0));
      // A two-way motorway mapped as one line: a barrier down the middle.
      if (!oneway) c = mix(c, vec3(0.3, 0.3, 0.28), roadLine(x, 0.0, 0.6));
    } else if (style == 1) {
      c = mix(c, white, roadLine(edge, outer, 0.15));
      c = mix(c, white, roadDividers(lanes, x, along, 0.15, 3.0, 9.0));
      if (!oneway) c = mix(c, white, roadLine(x, 0.0, 0.15) * roadDash(along, 3.0, 9.0));
    } else if (style == 2) {
      if (oneway) c = mix(c, white, roadLine(edge, outer, 0.12));
      else c = mix(c, white, roadLine(x, 0.0, 0.12) * roadDash(along, 3.0, 9.0));
    }
    if (style <= 3) c = mix(c, white * 1.05, roadZebra(zebra, x, along, hw));
  } else if (style == 5) {
    // Pedestrian street: stone paving with joints.
    vec2 cell = vec2(x, along) / 0.9;
    float joint = max(roadLine(fract(cell.x), 0.0, 0.08), roadLine(fract(cell.y), 0.0, 0.08));
    c = vec3(0.6, 0.54, 0.43) * (0.94 + 0.08 * roadHash(floor(cell))) * (1.0 - 0.15 * joint);
  } else if (style == 6) {
    // Footway, and footway and cycleway beside a road: asphalt, a shade
    // paler than a street's, between kerbstones.
    c = vec3(0.25, 0.255, 0.26) * grain;
    c = mix(c, vec3(0.52, 0.51, 0.48), smoothstep(hw - 0.2, hw - 0.14, edge));
  } else if (style == 7) {
    // Cycleway: red asphalt with a dashed white centre.
    c = vec3(0.36, 0.12, 0.09) * grain;
    c = mix(c, white, roadLine(x, 0.0, 0.1) * roadDash(along, 1.0, 3.0));
  } else if (style == 8) {
    // Path: trodden earth, worn paler down the middle.
    c = mix(vec3(0.34, 0.25, 0.13), vec3(0.47, 0.36, 0.2), 1.0 - smoothstep(0.1, hw, edge));
    c *= 0.9 + 0.2 * roadNoise(world * 2.3);
  } else if (style == 9) {
    // Farm track: two wheel ruts, grass down the middle and along the edges.
    c = vec3(0.36, 0.26, 0.13) * (0.9 + 0.2 * roadNoise(world * 2.1));
    float grass = max(1.0 - smoothstep(0.25, 0.4, edge), smoothstep(hw - 0.35, hw - 0.15, edge));
    c = mix(c, vec3(0.1, 0.3, 0.05), grass);
  } else if (style == 10) {
    // Steps: stone treads with a shadowed riser every 32 cm.
    c = vec3(0.5, 0.46, 0.39) * (fract(along / 0.32) < 0.22 ? 0.72 : 1.0);
  } else if (style == 12) {
    // Tram rails set in the street: a polished head with its dark groove.
    // Everything else is discarded, see roadVisible.
    c = mix(vec3(0.2, 0.2, 0.21), vec3(0.62, 0.64, 0.68), roadLine(edge, 0.745, 0.035));
  } else {
    // Railway: ballast, sleepers, and two polished rails.
    c = vec3(0.26, 0.24, 0.22) * (0.85 + 0.3 * roadHash(floor(world * 6.0)));
    float sleeper = (edge < 1.25 && fract(along / 0.65) < 0.36) ? 1.0 : 0.0;
    c = mix(c, vec3(0.13, 0.085, 0.05), sleeper);
    c = mix(c, vec3(0.55, 0.58, 0.62), roadLine(edge, 0.72, 0.08));
  }
  return c;
}
`;

/** The one material for every road, path and railway in the world. */
export function createRoadMaterial(): MeshStandardMaterial {
  const material = new MeshStandardMaterial({
    color: new Color(0xffffff),
    roughness: 0.92,
    metalness: 0,
    side: DoubleSide,
    polygonOffset: true,
    polygonOffsetFactor: -2,
    polygonOffsetUnits: -2,
  });
  material.onBeforeCompile = (shader: WebGLProgramParametersWithUniforms) => {
    shader.uniforms.uWet = terrainWeather.wet;
    shader.uniforms.uRoadSnow = terrainWeather.roadSnow;
    shader.uniforms.uCloudShadow = terrainWeather.cloudShadow;
    shader.uniforms.uCloudDrift = terrainWeather.cloudDrift;
    shader.uniforms.uNight = floraNight;
    shader.uniforms.uLampMap = lampLight.map;
    shader.uniforms.uLampOrigin = lampLight.origin;
    shader.uniforms.uLampInvExtent = lampLight.invExtent;
    shader.vertexShader = shader.vertexShader
      .replace('#include <common>', `#include <common>
attribute vec4 aRoad;
attribute vec2 aZebra;
varying vec4 vRoad;
varying vec2 vZebra;
varying vec3 vRoadWorld;`)
      .replace('#include <worldpos_vertex>', `#include <worldpos_vertex>
  vRoad = aRoad;
  vZebra = aZebra;
  vRoadWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;`);
    shader.fragmentShader = shader.fragmentShader
      .replace('#include <common>', `#include <common>
uniform float uWet;
uniform float uRoadSnow;
uniform float uCloudShadow;
uniform vec2 uCloudDrift;
uniform float uNight;
varying vec4 vRoad;
varying vec2 vZebra;
varying vec3 vRoadWorld;
${GLSL_ROAD}
${GLSL_LAMP_LIGHT}`)
      .replace('#include <color_fragment>', `#include <color_fragment>
  float roadRough = 0.92;
  float roadNightGlow = 0.0;
  {
    float hw = vRoad.w;
    float across = (vRoad.x - 0.5) * 2.0 * hw;
    int style = int(vRoad.z + 0.5);
    if (!roadVisible(style, across)) discard;
    vec3 rc = roadColour(style, across, vRoad.y, hw, vRoadWorld.xz, vZebra);
    if (uWet > 0.005) {
      rc *= 1.0 - 0.22 * uWet;
      float puddle = smoothstep(0.4, 0.68, roadNoise(vRoadWorld.xz * 0.14));
      roadRough = mix(0.92, mix(0.42, 0.14, puddle), uWet);
    }
    if (uRoadSnow > 0.005) {
      // Snow on the road: in patches, thicker toward the edges, where the
      // wheels do not run and the plough leaves a rim.
      float n = roadNoise(vRoadWorld.xz * 0.7) * 0.6 + roadNoise(vRoadWorld.xz * 2.9) * 0.4;
      float edge = smoothstep(0.55, 0.95, abs(vRoad.x - 0.5) * 2.0);
      float cover = smoothstep(1.0 - uRoadSnow, 1.15 - uRoadSnow, n + edge * 0.35);
      rc = mix(rc, vec3(0.8, 0.84, 0.9), cover);
      roadRough = mix(roadRough, 0.7, cover);
    }
    if (uCloudShadow > 0.01) {
      float cPatch = roadNoise((vRoadWorld.xz - uCloudDrift) * 0.0032);
      rc *= 1.0 - smoothstep(0.44, 0.74, cPatch) * uCloudShadow * 0.26;
    }
    if (style <= 7 && uNight > 0.005) {
      roadNightGlow = 0.08 * uNight;
    }
    diffuseColor.rgb *= rc;
  }`)
      .replace('#include <roughnessmap_fragment>', `#include <roughnessmap_fragment>
  roughnessFactor = roadRough;`)
      .replace('#include <emissivemap_fragment>', `#include <emissivemap_fragment>
  totalEmissiveRadiance += streetLampRadiance(vRoadWorld.xz, diffuseColor.rgb, uNight, uWet)
    + diffuseColor.rgb * vec3(1.0, 0.84, 0.58) * roadNightGlow;`);
  };
  return material;
}
